/*
  Mobile sticky add to cart.

  Shows a fixed buy bar once the main buy button has scrolled out of view, on
  the product template below 750px only.

  Three things drive it, each from the source that actually owns the fact:

  - Visibility: sampled from the main buy button's position on scroll. The bar
    is the stand-in for that button, so the button's own position is the
    condition, not a scroll offset that would have to be re-guessed per layout.

    Deliberately not an IntersectionObserver. That fires on intersection
    changes, and going from above the viewport straight to below it is
    isIntersecting false to false - no change, so no callback. A fast flick from
    deep in the page back to the top skips the button entirely between ticks and
    leaves the bar stuck on with nothing to switch it off again. Sampling the
    position cannot miss a state, because it reads where the button is rather
    than when it crossed.

  - Price, selection and thumbnail: Dawn's variantChange event, which carries
    both the variant and the re-rendered section markup. The price is copied
    out of that markup rather than formatted here, so sale pricing, unit
    pricing and the store's money format stay identical to the main block
    instead of being reimplemented and drifting.

  - Availability: mirrored from the main submit button with a MutationObserver,
    written straight onto this bar's own button. Deriving it from
    variant.available would miss setUnavailable(), which Dawn calls for an
    impossible option combination and which publishes no event at all. Mirroring
    means there is one source of truth for "can this be bought" and the bar
    cannot disagree with the button it stands in for.
*/
(function () {
  if (customElements.get('sticky-atc')) return;

  const MOBILE = '(max-width: 749px)';

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('button[name="add"]');
      this.productForm = this.querySelector('product-form');

      this.mainButton = document.getElementById(this.dataset.target);
      if (!this.mainButton) return;

      this.mq = window.matchMedia(MOBILE);
      this.onBreakpoint = () => this.applyBreakpoint();
      this.mq.addEventListener('change', this.onBreakpoint);

      this.mirrorAvailability();
      this.watchVariant();
      this.applyBreakpoint();
    }

    disconnectedCallback() {
      this.mq?.removeEventListener('change', this.onBreakpoint);
      this.unwatchTarget();
      this.availabilityObserver?.disconnect();
      this.unsubscribeVariant?.();
      this.setActive(false);
    }

    /*
      Above 750px the bar is display:none in CSS, but the observer and the body
      padding are not CSS - leaving them running would reserve space at the foot
      of a desktop page for a bar that is not there.
    */
    applyBreakpoint() {
      if (this.mq.matches) {
        this.watchTarget();
        this.update();
      } else {
        this.unwatchTarget();
        this.setActive(false);
      }
    }

    watchTarget() {
      if (this.onScroll) return;

      /*
        Passive, and unthrottled for the same reason as the scroll to top
        button: the handler reads one rect and compares it to a boolean, and the
        class write only happens on the two frames where the state flips.
        Resize matters too - a rotation or an address bar collapsing moves the
        button without any scrolling.
      */
      this.onScroll = () => this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    }

    unwatchTarget() {
      if (!this.onScroll) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.onScroll = null;
    }

    /*
      Below the fold is not "scrolled out of view". At the top of a product page
      the buy button has simply not been reached yet, and putting the bar up
      there would cover the product on first sight to stand in for a button
      nobody has passed. Only the button having gone off the top counts.
    */
    update() {
      const scrolledPast = this.mainButton.getBoundingClientRect().bottom <= 0;
      this.setActive(scrolledPast);
    }

    setActive(active) {
      if (active === this.active) return;
      this.active = active;

      this.classList.toggle('sticky-atc--active', active);

      /*
        The padding goes on <body> rather than on the section. Only the body's
        bottom edge is the document's bottom edge, so this is the one container
        whose padding stops the bar covering the last of the page - putting it
        on the product section would leave the footer running underneath.

        The height is measured rather than hardcoded, because the title wraps to
        two lines on a narrow screen and the bar is taller when it does.
      */
      if (active) {
        document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
        document.body.classList.add('sticky-atc-active');
      } else {
        document.body.classList.remove('sticky-atc-active');
        document.documentElement.style.removeProperty('--sticky-atc-height');
      }
    }

    mirrorAvailability() {
      /*
        The button is driven directly rather than through ProductForm's
        toggleSubmitButton. Optional chaining guards a missing element, not a
        missing method: whenever this element is upgraded before the
        <product-form> inside it, `this.productForm` is a plain HTMLElement and
        the call throws rather than skipping. The dev server's hot reload hits
        that ordering on every section replace, and Dawn's handleSwapProduct
        replaces the same markup in production.

        Copying the state and the label straight off the main button also keeps
        one source of truth for what the button says, instead of this component
        re-deriving the wording from window.variantStrings.
      */
      const apply = () => {
        if (!this.button) return;
        const disabled = this.mainButton.hasAttribute('disabled');
        const text = this.mainButton.querySelector('span')?.textContent;
        this.button.toggleAttribute('disabled', disabled);
        const label = this.button.querySelector('span');
        if (label && text) label.textContent = text;
      };

      this.availabilityObserver = new MutationObserver(apply);
      this.availabilityObserver.observe(this.mainButton, { attributes: true, attributeFilter: ['disabled'] });
      apply();
    }

    watchVariant() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        /*
          Other product sections publish this too - recommendations, recently
          viewed. Without the section check, choosing a variant in one of those
          would rewrite this bar with a different product's price.
        */
        if (event?.data?.sectionId !== this.sectionId) return;

        const { variant, html } = event.data;
        if (!variant) return;

        const input = this.querySelector('[data-sticky-variant-id]');
        if (input) {
          input.value = variant.id;
          input.disabled = !variant.available;
        }

        const price = this.querySelector('[data-sticky-price]');
        const source = html?.getElementById(`price-${this.sectionId}`);
        if (price && source) price.innerHTML = source.innerHTML;

        const label = this.querySelector('[data-sticky-variant]');
        if (label) label.textContent = variant.title;

        const image = this.querySelector('[data-sticky-media] img');
        if (image && variant.featured_image?.src) {
          image.src = variant.featured_image.src;

          /*
            The srcset still points at the previous variant's file and would
            win over the new src at any density above 1. Clearing it lets src
            take effect; the bar's thumbnail is 48px, so there is nothing to
            lose by serving one size.
          */
          image.removeAttribute('srcset');
        }

        /*
          Re-measured because a different variant title can change the wrap and
          so the bar's height, and the body padding has to follow it.
        */
        if (this.active) {
          document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
        }
      });
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();
